<template>
  <div class="qt">
    <!-- Kopf -->
    <div class="qt__head">
      <div class="qt__tabs" v-if="tickets.length > 1">
        <button v-for="t in tickets" :key="t.id" class="qt__tab" :class="{ 'qt__tab--active': t.id === activeId }" @click="activeId = t.id">
          <span class="qt__dot" :class="'qt__dot--' + stateOf(t)"></span>{{ t.subject || 'Ohne Betreff' }}
        </button>
      </div>
      <div v-if="active" class="qt__titleRow">
        <div class="qt__title">
          <span class="qt__state" :class="'qt__state--' + stateOf(active)">{{ stateLabel(active) }}</span>
          <span v-if="active.priority === 'high'" class="qt__chip qt__chip--prio"><v-icon x-small color="#c62828">mdi-fire</v-icon>Hoch</span>
          <span v-if="active.category && active.category.name" class="qt__chip">{{ active.category.name }}</span>
          <span v-if="active.brand && active.brand.alias" class="qt__chip qt__chip--muted">{{ active.brand.alias }}</span>
          <span v-if="active.source === 'external'" class="qt__chip qt__chip--muted">Status-Seite</span>
          <span v-if="sending" class="qt__chip qt__chip--run"><v-progress-circular indeterminate size="10" width="2" color="#1565c0" class="mr-1" />wird gesendet</span>
        </div>
        <div class="qt__titleActions">
          <v-btn v-if="hasTranslations" text x-small class="qt__toggle" @click="showOriginal = !showOriginal" :title="showOriginal ? 'Übersetzung anzeigen' : 'Originaltext anzeigen'">
            <v-icon x-small left>mdi-translate</v-icon>{{ showOriginal ? 'Original' : 'Übersetzt' }}
          </v-btn>
          <v-btn icon small :href="active.url" target="_blank" title="In Quelp öffnen"><v-icon small color="#1e88e5">mdi-alpha-q-box</v-icon></v-btn>
          <v-menu offset-y left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" :loading="busy === 'menu'"><v-icon small>mdi-dots-vertical</v-icon></v-btn>
            </template>
            <v-list dense>
              <v-list-item @click="act('hold', { onHold: !active.onHold }, 'menu')">
                <v-list-item-icon class="mr-2"><v-icon small>{{ active.onHold ? 'mdi-play-circle-outline' : 'mdi-pause-circle-outline' }}</v-icon></v-list-item-icon>
                <v-list-item-title>{{ active.onHold ? 'Hold lösen' : 'Bestellung anhalten' }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="act('priority', { priority: active.priority === 'high' ? 'normal' : 'high' }, 'menu')">
                <v-list-item-icon class="mr-2"><v-icon small>mdi-fire</v-icon></v-list-item-icon>
                <v-list-item-title>{{ active.priority === 'high' ? 'Priorität normal' : 'Priorität hoch' }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="active.status !== 'open'" @click="act('reopen', {}, 'menu')">
                <v-list-item-icon class="mr-2"><v-icon small>mdi-email-open-outline</v-icon></v-list-item-icon>
                <v-list-item-title>Wieder öffnen</v-list-item-title>
              </v-list-item>
              <v-list-item @click="act('get', {}, 'menu')">
                <v-list-item-icon class="mr-2"><v-icon small>mdi-refresh</v-icon></v-list-item-icon>
                <v-list-item-title>Aus Quelp neu laden</v-list-item-title>
              </v-list-item>
              <v-list-item @click="refresh">
                <v-list-item-icon class="mr-2"><v-icon small color="#7b5cd6">mdi-auto-fix</v-icon></v-list-item-icon>
                <v-list-item-title>Vorschläge &amp; Übersetzungen neu erzeugen</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
      <div v-if="active" class="qt__subject">{{ active.subject || 'Ohne Betreff' }}</div>
      <div v-if="active" class="qt__meta">
        <span>{{ active.customer && (active.customer.name || active.customer.email) }}</span>
        <span v-if="active.customer && active.customer.language">· schreibt {{ String(active.customer.language).toUpperCase() }}</span>
        <span v-if="active.receivedAt">· Ticket seit {{ ago(active.receivedAt) }}</span>
        <span v-if="active.onHold" class="qt__hold"><v-icon x-small color="#b26a00">mdi-pause-circle</v-icon> Bestellung on hold{{ active.holdBlocked ? ' (blockiert)' : '' }}</span>
      </div>
      <!-- Weitere Tickets dieses Kunden -->
      <div v-if="active && otherTickets.length" class="qt__others">
        <span class="qt__othersLabel"><v-icon x-small>mdi-account-multiple-outline</v-icon> {{ otherTickets.length }} weitere{{ otherTickets.length === 1 ? 's Ticket' : ' Tickets' }} dieses Kunden:</span>
        <button v-for="t in otherTickets.slice(0, 6)" :key="t.id" type="button" class="qt__otherChip" :class="{ 'qt__otherChip--open': t.status === 'open' }" :title="t.subject" @click="openOther(t)">
          <span class="qt__dot" :class="'qt__dot--' + stateOf(t)"></span>{{ t.order && t.order.name ? t.order.name + ' · ' : '' }}{{ (t.subject || 'Ohne Betreff').slice(0, 34) }}<span class="qt__otherDate">{{ ago(t.receivedAt) }}</span>
        </button>
      </div>
    </div>

    <!-- Verlauf -->
    <div class="qt__thread" ref="thread">
      <template v-if="active">
        <div v-if="!messages.length" class="qt__muted">Noch keine Nachrichten.</div>
        <div v-for="m in messages" :key="m.id" class="qt__msg" :class="{ 'qt__msg--ours': m.direction === 'outbound' }">
          <div class="qt__msgHead">
            <b>{{ m.direction === 'outbound' ? (m.sentBy ? labelFor(m.sentBy) : m.from || 'Support') : (m.from || m.fromEmail) }}</b>
            <span class="qt__msgHeadRight">
              <span v-if="m.textTranslated && !showOriginal" class="qt__lang">{{ (active.customer && active.customer.language ? active.customer.language : '??').toUpperCase() }} → {{ (active.workspaceLanguage || 'de').toUpperCase() }}</span>
              <span class="qt__msgDate" :title="m.date">{{ m.date ? ago(m.date) : '' }}</span>
            </span>
          </div>
          <div class="qt__msgBody">{{ m.textTranslated && !showOriginal ? m.textTranslated : m.text }}</div>
          <div v-if="m.attachments && m.attachments.length" class="qt__atts">
            <a v-for="a in m.attachments" :key="a.id" :href="a.url" target="_blank" class="qt__att" :title="a.filename">
              <img v-if="isImage(a) && a.url" :src="a.url" :alt="a.filename" />
              <span v-else><v-icon x-small>mdi-paperclip</v-icon>{{ a.filename }}</span>
            </a>
          </div>
        </div>
      </template>
      <template v-else-if="legacy.length">
        <div class="qt__muted mb-2">
          <template v-if="ticket.quelpId"><v-progress-circular v-if="loadingMissing" indeterminate size="12" width="2" color="#7b5cd6" class="mr-1" />Quelp-Ticket wird geladen… Bis dahin die Nachricht aus dem Hold-Aufruf:</template>
          <template v-else>Nachrichten der Status-Seite (noch kein Quelp-Ticket):</template>
        </div>
        <div v-for="(m, i) in legacy" :key="i" class="qt__msg" :class="{ 'qt__msg--ours': m.fromSupport }">
          <div class="qt__msgHead"><b>{{ m.fromSupport ? (m.agent || 'Support') : 'Kunde' }}</b><span class="qt__msgDate">{{ ago(m.created) }}</span></div>
          <div class="qt__msgBody">{{ m.message }}</div>
          <div v-if="(m.images && m.images.length) || (m.imageUrls && m.imageUrls.length)" class="qt__atts">
            <a v-for="(u, j) in [...(m.imageUrls || []), ...(m.images || [])]" :key="j" :href="u" target="_blank" class="qt__att"><img :src="u" alt="" /></a>
          </div>
        </div>
      </template>
      <div v-else class="qt__muted">Kein Ticket vorhanden.</div>
    </div>

    <!-- Aktionen (wie Quelp: empfohlene zuerst, Rest hinter „weitere") -->
    <div v-if="active" class="qt__actions">
      <span class="qt__sectionLabel"><v-icon x-small color="#7b5cd6">mdi-lightning-bolt-outline</v-icon> Aktionen</span>
      <template v-if="actionsLoading && !actions.length"><v-progress-circular indeterminate size="12" width="2" color="#7b5cd6" /></template>
      <template v-else>
        <button v-for="a in visibleActions" :key="a.id" type="button" class="qt__actionBtn" :class="{ 'qt__actionBtn--rec': recommendedIds.has(a.id), 'qt__actionBtn--done': !!executedMap[a.id] }" :title="(a.description || '') + (executedMap[a.id] ? ' · ausgeführt ' + ago(executedMap[a.id].executedAt) : '')" @click="openAction(a)">
          <v-icon v-if="executedMap[a.id]" x-small :color="executedMap[a.id].success ? '#2e7d32' : '#c62828'" class="mr-1">{{ executedMap[a.id].success ? 'mdi-check' : 'mdi-close' }}</v-icon>
          <v-icon v-else-if="recommendedIds.has(a.id)" x-small color="#6a4fc4" class="mr-1">mdi-star</v-icon>
          {{ a.label }}<span class="qt__actionType">{{ a.type === 'shopify' ? 'Shopify' : 'TaskDash' }}</span>
        </button>
        <button v-if="restActions.length" type="button" class="qt__moreBtn" @click="showAllActions = !showAllActions">{{ showAllActions ? 'weniger' : `+${restActions.length} weitere` }}</button>
      </template>
    </div>

    <!-- Antwort -->
    <div v-if="active" class="qt__composer">
      <div v-if="suggestions.length" class="qt__suggestions">
        <span class="qt__sectionLabel"><v-icon x-small color="#7b5cd6">mdi-auto-fix</v-icon> Vorschläge</span>
        <button v-for="sg in suggestions" :key="sg.id" type="button" class="qt__sugg" :class="{ 'qt__sugg--active': chosenSuggestionId === sg.id }" :title="sg.approach" @click="useSuggestion(sg)">
          <v-icon v-if="sg.warnings && sg.warnings.length" x-small color="#b26a00" class="mr-1" :title="sg.warnings.join(' · ')">mdi-alert</v-icon>{{ sg.label || 'Vorschlag' }}
        </button>
      </div>
      <div v-if="chosenSuggestion && chosenSuggestion.approach" class="qt__suggHint">{{ chosenSuggestion.approach }}</div>
      <QuelpEditor ref="editor" v-model="draft" :on-ai="optimizeHtml" :disabled="sending" placeholder="Antwort in deiner Sprache – Quelp übersetzt beim Senden in die Kundensprache." />
      <div v-if="files.length" class="qt__files">
        <span v-for="(f, i) in files" :key="i" class="qt__chip qt__chip--muted"><v-icon x-small>mdi-paperclip</v-icon>{{ f.name }}<v-icon x-small class="ml-1" style="cursor:pointer" @click="files.splice(i, 1)">mdi-close</v-icon></span>
      </div>
      <div class="qt__composerRow">
        <div class="qt__composerLeft">
          <v-btn icon small title="Anhang" @click="$refs.file.click()"><v-icon small>mdi-paperclip</v-icon></v-btn>
          <input ref="file" type="file" multiple hidden accept="image/*,.pdf" @change="onFiles" />
          <v-btn text x-small class="qt__ai" :loading="busy === 'assist'" :disabled="draftEmpty" @click="assist('generate')" title="Stichpunkte zu einer fertigen Antwort ausformulieren"><v-icon x-small left color="#7b5cd6">mdi-auto-fix</v-icon>Ausformulieren</v-btn>
          <v-btn text x-small class="qt__ai" :disabled="sending || (active.status !== 'open' && !active.onHold)" @click="requestClose" :title="active.status === 'open' ? 'Ohne Antwort schließen (löst den Hold)' : 'Ticket abschließen und Hold lösen'"><v-icon x-small left>mdi-check</v-icon>{{ active.status === 'open' ? 'Schließen' : 'Abschließen & Hold lösen' }}</v-btn>
        </div>
        <div class="qt__composerRight">
          <v-btn small depressed rounded color="#edeafa" class="qt__btnSoft" :disabled="draftEmpty || sending" @click="requestSend(true)" title="Senden, Bestellung bleibt angehalten"><v-icon small left color="#6a4fc4">mdi-pause</v-icon>Senden &amp; Holden</v-btn>
          <v-btn small depressed rounded color="primary" :disabled="draftEmpty || sending" :loading="busy === 'upload'" @click="requestSend(false)"><v-icon small left>mdi-send</v-icon>Senden</v-btn>
        </div>
      </div>
      <div v-if="error" class="qt__error">{{ error }}</div>
    </div>

    <!-- Dialog: weitere offene Tickets des Kunden mitschließen -->
    <v-dialog :value="!!closeOthers" max-width="560" @input="(v) => !v && (closeOthers = null)">
      <v-card class="qt__dlg" v-if="closeOthers">
        <div class="qt__dlgTitle">{{ closeOthers.tickets.length === 1 ? 'Der Kunde hat noch ein weiteres offenes Ticket' : `Der Kunde hat noch ${closeOthers.tickets.length} weitere offene Tickets` }}</div>
        <div class="qt__dlgHint">Soll {{ closeOthers.mode === 'close' ? 'beim Schließen' : 'mit dieser Antwort' }} auch geschlossen werden? Abgewählte bleiben offen.</div>
        <label v-for="t in closeOthers.tickets" :key="t.id" class="qt__dlgItem" :class="{ 'qt__dlgItem--on': closeOthers.selected.includes(t.id) }">
          <v-simple-checkbox :value="closeOthers.selected.includes(t.id)" color="#7b5cd6" @input="toggleCloseOther(t.id)" />
          <div class="qt__dlgItemBody">
            <div class="qt__dlgItemTitle">{{ t.order && t.order.name ? t.order.name + ' · ' : '' }}{{ t.subject || 'Ohne Betreff' }}<span class="qt__otherDate">{{ ago(t.receivedAt) }}</span></div>
            <div class="qt__dlgItemPreview">{{ t.lastMessage ? t.lastMessage.preview : '' }}</div>
          </div>
        </label>
        <div class="qt__dlgFoot">
          <v-btn text small @click="closeOthers = null">Abbrechen</v-btn>
          <v-spacer />
          <v-btn text small color="#6a4fc4" @click="confirmOthers(false)">{{ closeOthers.mode === 'close' ? 'Nur dieses schließen' : 'Nur senden' }}</v-btn>
          <v-btn small depressed rounded color="primary" :disabled="!closeOthers.selected.length" @click="confirmOthers(true)">{{ closeOthers.mode === 'close' ? 'Alle gewählten schließen' : 'Senden & gewählte schließen' }}</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <QuelpActionDialog v-if="actionDialog" :show="!!actionDialog" :action="actionDialog" :execute="executeAction" @close="actionDialog = null" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { httpsCallable } from "firebase/functions";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import QuelpEditor from "@/components/Support/QuelpEditor";
import QuelpActionDialog from "@/components/Support/QuelpActionDialog";
import { functions } from "../../../fb";
import { getDateFromNowShort, currentMonthYear } from "@/utils";

// Rechte Spalte der Support-Ansicht: Quelp-Verlauf + Antwort über die Quelp-API
// (quelpAction). Versand läuft über die Sendewarteschlange (store/sendQueue) im
// Hintergrund; weitere offene Tickets des Kunden können mitgeschlossen werden.
const stripHtml = (html) =>
  String(html || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

export default {
  name: "QuelpThread",
  components: { QuelpEditor, QuelpActionDialog },
  props: {
    ticket: { type: Object, required: true },
    compact: { type: Boolean, default: false },
  },
  data() {
    return {
      activeId: null,
      draft: "",
      files: [],
      busy: null,
      error: "",
      showOriginal: false,
      chosenSuggestionId: null,
      actions: [],
      actionsLoading: false,
      actionsForTicket: null,
      actionDialog: null,
      showAllActions: false,
      customerTickets: [],
      customerTicketsFor: null,
      closeOthers: null, // { mode: 'send'|'close', hold, tickets, selected[] }
      loadingMissing: false,
      missingLoadedFor: null,
    };
  },
  computed: {
    ...mapGetters(["isTicketSending"]),
    tickets() {
      const map = this.ticket.quelpTickets || {};
      return Object.values(map).sort((a, b) => {
        const score = (t) => (t.status === "open" ? 2 : 0) + (t.onHold ? 1 : 0);
        return score(b) - score(a) || String(b.updatedAt || "").localeCompare(String(a.updatedAt || ""));
      });
    },
    active() {
      return this.tickets.find((t) => t.id === this.activeId) || this.tickets[0] || null;
    },
    sending() {
      return !!(this.active && this.isTicketSending(this.active.id));
    },
    messages() {
      return (this.active && this.active.messages) || [];
    },
    hasTranslations() {
      return this.messages.some((m) => !!m.textTranslated);
    },
    suggestions() {
      return (this.active && this.active.suggestions) || [];
    },
    chosenSuggestion() {
      return this.suggestions.find((s) => s.id === this.chosenSuggestionId) || null;
    },
    draftEmpty() {
      return !stripHtml(this.draft);
    },
    legacy() {
      const d = this.ticket.data || {};
      return Object.values(d)
        .flatMap((x) => (x && Array.isArray(x.messages) ? x.messages : []))
        .sort((a, b) => String(a.created || "").localeCompare(String(b.created || "")));
    },
    otherTickets() {
      if (!this.active) return [];
      const ids = new Set(this.tickets.map((t) => t.id));
      return this.customerTickets.filter((t) => t.id !== this.active.id && !ids.has(t.id)).sort((a, b) => String(b.receivedAt || "").localeCompare(String(a.receivedAt || "")));
    },
    otherOpenTickets() {
      return this.otherTickets.filter((t) => t.status === "open" && !this.isTicketSending(t.id));
    },
    recommendedIds() {
      const src = this.chosenSuggestion || this.suggestions[0];
      return new Set((src && src.recommendedActionIds) || []);
    },
    executedMap() {
      const map = {};
      ((this.active && this.active.executedActions) || []).forEach((a) => (map[a.actionId] = a));
      return map;
    },
    rankedActions() {
      const rank = (a) => (this.executedMap[a.id] ? 0 : this.recommendedIds.has(a.id) ? 1 : 2);
      return [...this.actions].sort((a, b) => rank(a) - rank(b));
    },
    primaryActions() {
      return this.rankedActions.filter((a) => this.executedMap[a.id] || this.recommendedIds.has(a.id));
    },
    restActions() {
      return this.rankedActions.filter((a) => !this.executedMap[a.id] && !this.recommendedIds.has(a.id));
    },
    visibleActions() {
      if (this.showAllActions || !this.primaryActions.length) return this.showAllActions ? this.rankedActions : this.rankedActions.slice(0, 3);
      return this.primaryActions;
    },
  },
  watch: {
    "ticket.orderUid": {
      immediate: true,
      handler() {
        this.activeId = this.tickets[0] ? this.tickets[0].id : null;
        this.resetComposer();
        this.loadMissingTicket();
      },
    },
    "active.id": {
      immediate: true,
      handler(id, old) {
        if (id && id !== old) {
          this.resetComposer();
          this.loadActions();
          this.loadCustomerTickets();
        }
      },
    },
    "messages.length"() {
      this.$nextTick(this.scrollDown);
    },
    active: {
      immediate: true,
      handler(t) {
        if (t && t.status === "open" && t.read === false) this.act("read", { read: true }, null, true);
      },
    },
  },
  methods: {
    ...mapActions(["enqueueSend"]),
    ago(d) {
      return d ? getDateFromNowShort(d) : "";
    },
    isImage(a) {
      return String(a.mimeType || "").startsWith("image/");
    },
    labelFor(sentBy) {
      const m = String(sentBy).match(/^([^:]+):(.+)$/);
      if (m) return `${m[2].split("@")[0]} (${m[1]})`;
      return String(sentBy).split("@")[0];
    },
    stateOf(t) {
      if (t.status === "open") return "open";
      return t.onHold ? "hold" : "done";
    },
    stateLabel(t) {
      const s = this.stateOf(t);
      return s === "open" ? "Offen" : s === "hold" ? "Beantwortet · Hold" : "Beantwortet";
    },
    resetComposer() {
      this.draft = "";
      this.files = [];
      this.error = "";
      this.chosenSuggestionId = null;
      this.showOriginal = false;
      this.showAllActions = false;
      this.closeOthers = null;
      this.$nextTick(this.scrollDown);
    },
    scrollDown() {
      const el = this.$refs.thread;
      if (el) el.scrollTop = el.scrollHeight;
    },
    onFiles(e) {
      const list = Array.from(e.target.files || []).slice(0, 5 - this.files.length);
      this.files.push(...list);
      e.target.value = "";
    },
    async uploadFiles() {
      if (!this.files.length) return [];
      const storage = getStorage();
      const out = [];
      for (const [i, f] of this.files.entries()) {
        const safe = f.name.replace(/[^\w.\-]+/g, "_");
        const r = ref(storage, `/mailAttachments/${currentMonthYear}/quelp_${this.ticket.orderUid}_${Date.now()}_${i}_${safe}`);
        const up = await uploadBytes(r, f, { cacheControl: "public,max-age=300" });
        out.push({ filename: f.name, mimeType: f.type || "application/octet-stream", url: await getDownloadURL(up.ref) });
      }
      return out;
    },
    async call(payload) {
      const fn = httpsCallable(functions, "quelpAction", { timeout: 70000 });
      const res = await fn(payload);
      if (!res.data || res.data.status === "error") throw new Error((res.data && res.data.message) || "Quelp-Aktion fehlgeschlagen");
      return res.data;
    },
    async act(action, extra = {}, busyKey = "menu", silent = false) {
      if (!this.active) return;
      if (busyKey) this.busy = busyKey;
      this.error = "";
      try {
        const r = await this.call({ action, ticketId: this.active.id, ...extra });
        if (r && r.ticket) this.$emit("ticket-updated", r.ticket);
      } catch (e) {
        if (!silent) this.error = e.message;
      } finally {
        if (busyKey) this.busy = null;
      }
    },
    // Support-Doc kennt eine Quelp-ID (z.B. über /setHold), aber der Spiegel hat
    // das Ticket noch nicht → einmalig aus Quelp nachladen (füllt quelpTickets).
    async loadMissingTicket() {
      const qid = this.ticket.quelpId;
      if (!qid || this.tickets.length || this.missingLoadedFor === qid) return;
      this.missingLoadedFor = qid;
      this.loadingMissing = true;
      try {
        await this.call({ action: "syncOrder", orderId: this.ticket.orderUid });
      } catch (e) {
        console.warn("Quelp-Ticket nachladen fehlgeschlagen:", e.message);
      } finally {
        this.loadingMissing = false;
      }
    },
    // ── Weitere Tickets des Kunden ──
    async loadCustomerTickets() {
      const email = this.active && this.active.customer && this.active.customer.email;
      if (!email) {
        this.customerTickets = [];
        return;
      }
      if (this.customerTicketsFor === email) return;
      try {
        const r = await this.call({ action: "customerTickets", email });
        this.customerTickets = r.items || [];
        this.customerTicketsFor = email;
      } catch (e) {
        this.customerTickets = [];
      }
    },
    openOther(t) {
      if (t.order && t.order.id) this.$emit("open-order", String(t.order.id));
      else window.open(t.url, "_blank");
    },
    // ── Senden / Schließen mit Rückfrage ──
    requestSend(hold) {
      if (!this.active || this.draftEmpty || this.sending) return;
      if (this.otherOpenTickets.length) {
        this.closeOthers = { mode: "send", hold, tickets: this.otherOpenTickets, selected: this.otherOpenTickets.map((t) => t.id) };
        return;
      }
      this.performSend(hold, []);
    },
    requestClose() {
      if (!this.active || this.sending) return;
      if (this.otherOpenTickets.length) {
        this.closeOthers = { mode: "close", hold: this.active.onHold, tickets: this.otherOpenTickets, selected: this.otherOpenTickets.map((t) => t.id) };
        return;
      }
      this.performClose([]);
    },
    toggleCloseOther(id) {
      const sel = this.closeOthers.selected;
      this.closeOthers.selected = sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id];
    },
    confirmOthers(withOthers) {
      const d = this.closeOthers;
      if (!d) return;
      const ids = withOthers ? d.selected : [];
      this.closeOthers = null;
      if (d.mode === "send") this.performSend(d.hold, ids);
      else this.performClose(ids);
    },
    async performSend(hold, closeIds) {
      const t = this.active;
      this.busy = "upload";
      this.error = "";
      let attachments = [];
      try {
        attachments = await this.uploadFiles();
      } catch (e) {
        this.error = `Anhang-Upload fehlgeschlagen: ${e.message}`;
        this.busy = null;
        return;
      }
      this.busy = null;
      const label = (t.customer && (t.customer.name || t.customer.email)) || t.subject || t.id;
      this.enqueueSend({
        ticketId: t.id,
        orderUid: this.ticket.orderUid,
        label,
        mode: "send",
        payload: { action: "reply", ticketId: t.id, text: this.draft, attachments, hold, chosenSuggestionId: this.chosenSuggestionId || undefined },
        onDone: (r) => r && r.ticket && this.$emit("ticket-updated", r.ticket),
      });
      this.enqueueCloses(closeIds);
      this.draft = "";
      this.files = [];
      this.chosenSuggestionId = null;
      this.$emit("sent", { ticketId: t.id });
    },
    performClose(closeIds) {
      const t = this.active;
      const label = (t.customer && (t.customer.name || t.customer.email)) || t.subject || t.id;
      this.enqueueSend({
        ticketId: t.id,
        orderUid: this.ticket.orderUid,
        label,
        mode: "close",
        // Schließen löst den Hold (wie in Quelp); nur „Senden & Holden" behält ihn.
        payload: { action: "close", ticketId: t.id, hold: false },
        onDone: (r) => r && r.ticket && this.$emit("ticket-updated", r.ticket),
      });
      this.enqueueCloses(closeIds);
      this.$emit("sent", { ticketId: t.id, closed: true });
    },
    enqueueCloses(ids) {
      for (const id of ids) {
        const t = this.customerTickets.find((x) => x.id === id);
        if (!t) continue;
        this.enqueueSend({
          ticketId: t.id,
          orderUid: t.order && t.order.id ? String(t.order.id) : null,
          label: (t.customer && (t.customer.name || t.customer.email)) || t.subject || t.id,
          mode: "close",
          payload: { action: "close", ticketId: t.id, hold: false },
        });
      }
      if (ids.length) this.customerTicketsFor = null;
    },
    // ── KI ──
    async assist(mode) {
      if (!this.active || this.draftEmpty) return;
      this.busy = "assist";
      this.error = "";
      try {
        const r = await this.call({ action: "assist", ticketId: this.active.id, mode, text: stripHtml(this.draft) });
        if (r.html) this.draft = String(r.html);
      } catch (e) {
        this.error = e.message;
      } finally {
        this.busy = null;
      }
    },
    async optimizeHtml(html) {
      if (!this.active) return null;
      try {
        const r = await this.call({ action: "assist", ticketId: this.active.id, mode: "optimize", text: stripHtml(html) });
        return r.html ? String(r.html) : null;
      } catch (e) {
        this.error = e.message;
        return null;
      }
    },
    async refresh() {
      this.busy = "menu";
      this.error = "";
      try {
        await this.call({ action: "refresh", ticketId: this.active.id });
        this.$emit("notice", "Anreicherung angestoßen – Vorschläge und Übersetzungen erscheinen in wenigen Sekunden.");
      } catch (e) {
        this.error = e.message;
      } finally {
        this.busy = null;
      }
    },
    useSuggestion(sg) {
      this.chosenSuggestionId = sg.id;
      this.draft = sg.text || "";
    },
    // ── Aktionen ──
    async loadActions(force = false) {
      if (!this.active) return;
      if (!force && this.actionsForTicket === this.active.id && this.actions.length) return;
      this.actionsLoading = true;
      try {
        const r = await this.call({ action: "actions", ticketId: this.active.id });
        this.actions = r.actions || [];
        this.actionsForTicket = this.active.id;
      } catch (e) {
        this.error = e.message;
      } finally {
        this.actionsLoading = false;
      }
    },
    openAction(a) {
      this.actionDialog = a;
    },
    async executeAction(actionId, fields) {
      const r = await this.call({ action: "executeAction", ticketId: this.active.id, actionId, fields });
      if (r && r.ticket) this.$emit("ticket-updated", r.ticket);
      this.actionsForTicket = null;
      this.$emit("notice", "Aktion ausgeführt.");
      return r;
    },
  },
};
</script>

<style lang="scss" scoped>
.qt {
  display: flex; flex-direction: column; background: #fff; border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07);
  // Füllt den Elterncontainer (Grid-Zelle oder Flex-Spalte) ohne Prozent-Höhen:
  // Verlauf behält Mindesthöhe, der Antwortbereich schrumpft und scrollt selbst.
  min-height: 0; height: 100%; max-height: 100%; overflow: hidden; flex: 1 1 auto;
  &__head { flex: 0 0 auto; padding: 12px 16px 10px; border-bottom: 1px solid rgba(0, 0, 0, 0.06); display: flex; flex-direction: column; gap: 6px; }
  &__tabs { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 4px; }
  &__tab { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 600; border-radius: 999px; padding: 3px 10px; background: #f1f0f5; color: rgba(0, 0, 0, 0.6); border: 0; cursor: pointer; max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    &--active { background: #edeafa; color: #6a4fc4; } }
  &__dot { width: 8px; height: 8px; border-radius: 50%; background: #9e9e9e; flex: 0 0 auto;
    &--open { background: #e53935; } &--hold { background: #fb8c00; } &--done { background: #43a047; } }
  &__titleRow { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
  &__title { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  &__titleActions { display: flex; align-items: center; }
  &__toggle { text-transform: none; letter-spacing: 0; color: #6a4fc4 !important; font-weight: 600; }
  &__state { font-weight: 700; font-size: 11px; border-radius: 999px; padding: 2px 10px;
    &--open { background: #ffebee; color: #c62828; } &--hold { background: #fff3e0; color: #b26a00; } &--done { background: #e8f5e9; color: #2e7d32; } }
  &__chip { display: inline-flex; align-items: center; gap: 3px; font-size: 10px; font-weight: 700; border-radius: 999px; padding: 1px 8px; background: #edeafa; color: #6a4fc4;
    &--prio { background: #ffebee; color: #c62828; } &--muted { background: #f1f0f5; color: rgba(0, 0, 0, 0.55); } &--run { background: #e3f2fd; color: #1565c0; } }
  &__subject { font-weight: 800; font-size: 14px; color: #282828; }
  &__meta { font-size: 11px; color: rgba(0, 0, 0, 0.55); display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
  &__hold { color: #b26a00; font-weight: 600; display: inline-flex; align-items: center; gap: 3px; }
  &__others { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-top: 2px; }
  &__othersLabel { font-size: 11px; color: rgba(0, 0, 0, 0.55); display: inline-flex; align-items: center; gap: 3px; }
  &__otherChip { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; border-radius: 999px; padding: 2px 10px; background: #f1f0f5; color: rgba(0, 0, 0, 0.7); border: 0; cursor: pointer; max-width: 300px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    &--open { background: #ffebee; color: #c62828; } &:hover { filter: brightness(0.96); } }
  &__otherDate { margin-left: 6px; font-size: 10px; color: rgba(0, 0, 0, 0.45); }
  &__thread { flex: 1 1 160px; min-height: 160px; overflow-y: auto; padding: 12px 16px; display: flex; flex-direction: column; gap: 10px; background: #fafafa; }
  &__muted { font-size: 12px; color: rgba(0, 0, 0, 0.5); }
  &__msg { max-width: 88%; align-self: flex-start; background: #fff; border-radius: 14px 14px 14px 4px; padding: 8px 12px; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
    &--ours { align-self: flex-end; background: #edeafa; border-radius: 14px 14px 4px 14px; } }
  &__msgHead { display: flex; justify-content: space-between; gap: 12px; font-size: 11px; color: rgba(0, 0, 0, 0.6); margin-bottom: 2px; }
  &__msgHeadRight { display: inline-flex; align-items: center; gap: 6px; }
  &__lang { font-size: 10px; font-weight: 700; background: #edeafa; color: #6a4fc4; border-radius: 999px; padding: 0 6px; }
  &__msgDate { white-space: nowrap; color: rgba(0, 0, 0, 0.45); }
  &__msgBody { font-size: 13px; color: #282828; white-space: pre-wrap; word-break: break-word; }
  &__atts { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 6px; }
  &__att { display: inline-flex; align-items: center; gap: 3px; font-size: 11px; color: #6a4fc4; text-decoration: none; img { width: 72px; height: 72px; object-fit: cover; border-radius: 8px; display: block; } }
  &__sectionLabel { font-size: 11px; font-weight: 700; color: #6a4fc4; display: inline-flex; align-items: center; gap: 3px; margin-right: 2px; }
  &__actions { flex: 0 0 auto; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; padding: 8px 12px; border-top: 1px solid rgba(0, 0, 0, 0.06); }
  &__actionBtn { display: inline-flex; align-items: center; font-size: 12px; font-weight: 600; border-radius: 999px; padding: 3px 10px; background: #f1f0f5; color: #282828; border: 1px solid transparent; cursor: pointer;
    &:hover { background: #e9e7f1; }
    &--rec { background: #fff; border-color: rgba(123, 92, 214, 0.45); color: #4b3a9a; }
    &--done { background: #e8f5e9; color: #2e7d32; } }
  &__actionType { margin-left: 6px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: rgba(0, 0, 0, 0.4); }
  &__moreBtn { border: 0; background: transparent; font-size: 12px; color: #6a4fc4; font-weight: 600; cursor: pointer; }
  &__composer { flex: 0 1 auto; min-height: 0; overflow-y: auto; border-top: 1px solid rgba(0, 0, 0, 0.06); padding: 10px 12px 12px; display: flex; flex-direction: column; gap: 8px; }
  &__composer > * { flex: 0 0 auto; }
  &__suggestions { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  &__sugg { border: 1px solid rgba(123, 92, 214, 0.3); background: #fff; color: #282828; font-size: 12px; font-weight: 600; border-radius: 999px; padding: 3px 10px; cursor: pointer; display: inline-flex; align-items: center;
    &:hover { background: #f4f2fb; } &--active { background: #edeafa; border-color: #7b5cd6; color: #6a4fc4; } }
  &__suggHint { font-size: 11px; color: rgba(0, 0, 0, 0.6); font-style: italic; background: #fafafa; border-radius: 10px; padding: 6px 10px; }
  &__files { display: flex; gap: 6px; flex-wrap: wrap; }
  &__composerRow { display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
  &__composerLeft, &__composerRight { display: flex; align-items: center; gap: 6px; }
  &__ai { text-transform: none; letter-spacing: 0; color: #6a4fc4 !important; font-weight: 600; }
  &__btnSoft { color: #6a4fc4 !important; text-transform: none; letter-spacing: 0; font-weight: 600; }
  &__error { font-size: 12px; color: #c62828; }
  &__dlg { border-radius: 14px !important; padding: 16px; display: flex; flex-direction: column; gap: 8px; }
  &__dlgTitle { font-weight: 800; font-size: 15px; color: #282828; }
  &__dlgHint { font-size: 12px; color: rgba(0, 0, 0, 0.6); margin-bottom: 4px; }
  &__dlgItem { display: flex; gap: 8px; align-items: flex-start; border: 1px solid rgba(0, 0, 0, 0.08); border-radius: 10px; padding: 8px 10px; cursor: pointer;
    &--on { border-color: rgba(123, 92, 214, 0.45); background: #faf9fe; } }
  &__dlgItemBody { min-width: 0; }
  &__dlgItemTitle { font-size: 13px; font-weight: 600; color: #282828; }
  &__dlgItemPreview { font-size: 12px; color: rgba(0, 0, 0, 0.55); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &__dlgFoot { display: flex; align-items: center; gap: 6px; margin-top: 8px; }
}
</style>
